import { useEffect, useState } from 'react';
import { Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import HelpIcon from './HelpIcon';
import HelpPane from './HelpPane';
import { getHelpContent } from './helpContent';

const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  return typeof data === 'string' && data ? data : fallback;
};
const day = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

/**
 * Settings > Daily summary email: the user's own switch for the morning email about the current organization
 * (yesterday's sales and money, cash and bank, dues, what is waiting), a sample, and a copy now. Org Admin and Finance
 * Manager only; the caller decides whether to show it.
 */
function DailySummarySettings() {
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [sample, setSample] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    axios.get('/api/daily-summary/me')
      .then((r) => setStatus(r.data))
      .catch((error) => toast.error(errorMessage(error, 'Could not load the daily summary setting')));
  }, []);

  const toggle = (on) => {
    setSaving(true);
    axios.put('/api/daily-summary/me', { subscribed: on })
      .then((r) => {
        setStatus(r.data);
        toast.success(on ? 'You will get the daily summary each morning' : 'Daily summary turned off');
      })
      .catch((error) => toast.error(errorMessage(error, 'Could not change the daily summary')))
      .finally(() => setSaving(false));
  };

  const showSample = () => {
    setSample('');
    axios.get('/api/daily-summary/preview', { responseType: 'text' })
      .then((r) => setSample(r.data))
      .catch((error) => {
        setSample(null);
        toast.error(errorMessage(error, 'Could not make a sample'));
      });
  };

  const sendNow = () => {
    setSending(true);
    axios.post('/api/daily-summary/me/send')
      .then(() => toast.success(`Sent to ${status.email}`))
      .catch((error) => toast.error(errorMessage(error, 'Could not send the daily summary')))
      .finally(() => setSending(false));
  };

  const help = getHelpContent('daily-summary');

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex align-items-center">
        <h5 className="mb-0">Daily summary email</h5>
        {help && <HelpIcon onClick={() => setShowHelp(true)} />}
      </Card.Header>
      <Card.Body>
        {!status ? <Spinner animation="border" size="sm" /> : (
          <>
            <p className="text-muted small">
              Each morning at 8, yesterday&apos;s sales and the money that came in and went out, cash and bank, what is due
              to collect and to pay, and anything running low or waiting for approval or a quality check.
            </p>
            <Form.Check
              type="switch"
              id="daily-summary-switch"
              label={<>Email me the daily summary for this organization at <strong>{status.email}</strong></>}
              checked={!!status.subscribed}
              disabled={saving || !status.canReceive}
              onChange={(e) => toggle(e.target.checked)}
            />
            {status.subscribed && status.lastSentOn && (
              <div className="small text-muted mt-1">Last sent {day(status.lastSentOn)}</div>
            )}
            {!status.canReceive && (
              <div className="small text-muted mt-1">For the Org Admin and the Finance Manager of this organization.</div>
            )}
            <div className="d-flex gap-2 mt-3">
              <Button size="sm" variant="outline-secondary" onClick={showSample}>See a sample</Button>
              <Button size="sm" variant="outline-primary" onClick={sendNow} disabled={sending || !status.canReceive}>
                {sending ? 'Sending...' : 'Email me one now'}
              </Button>
            </div>
          </>
        )}
      </Card.Body>

      <Modal show={sample !== null} onHide={() => setSample(null)} size="lg">
        <Modal.Header closeButton><Modal.Title>Daily summary: sample from yesterday</Modal.Title></Modal.Header>
        <Modal.Body>
          {sample ? (
            <iframe title="Daily summary sample" sandbox="" srcDoc={sample} className="w-100 border rounded" style={{ height: 520, background: '#fff' }} />
          ) : <div className="text-center py-4"><Spinner animation="border" /></div>}
        </Modal.Body>
      </Modal>

      {help && (
        <HelpPane show={showHelp} onHide={() => setShowHelp(false)} title={help.title}>{help.content}</HelpPane>
      )}
    </Card>
  );
}

export default DailySummarySettings;
